import React, { useState, useRef, useEffect } from "react";
import { ChatCircleDots, X, PaperPlaneRight, Robot } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I'm Boot Buddy. Ask me anything about military pay, the GI Bill, TRICARE, branches, or post-service careers." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setMessages((m) => [...m, { role: "assistant", text: "" }]);
    setBusy(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionRef.current }),
      });
      if (!res.ok || !res.body) throw new Error("Network error");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assistantText = "";

      // SSE parsing
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const evt of events) {
          if (!evt.trim()) continue;
          const lines = evt.split("\n");
          let eventType = "message";
          let dataLines = [];
          for (const ln of lines) {
            if (ln.startsWith("event:")) eventType = ln.slice(6).trim();
            else if (ln.startsWith("data:")) dataLines.push(ln.slice(5).replace(/^ /, ""));
          }
          const data = dataLines.join("\n");
          if (eventType === "done") {
            sessionRef.current = data;
          } else if (eventType === "error") {
            assistantText += `\n\n[Error: ${data}]`;
            setMessages((m) => {
              const cp = [...m];
              cp[cp.length - 1] = { role: "assistant", text: assistantText };
              return cp;
            });
          } else {
            assistantText += data;
            setMessages((m) => {
              const cp = [...m];
              cp[cp.length - 1] = { role: "assistant", text: assistantText };
              return cp;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const cp = [...m];
        cp[cp.length - 1] = { role: "assistant", text: "Sorry, I couldn't reach the assistant. Please try again." };
        return cp;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="chatbot-open-btn"
        aria-label="Open Boot Buddy chat"
        className={`fixed bottom-6 left-6 h-16 w-16 bg-[#0033A0] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#002277] hover:scale-105 transition-transform z-40 ${open ? "hidden" : ""}`}
      >
        <ChatCircleDots size={28} weight="fill" />
      </button>

      {open && (
        <div className="fixed inset-x-4 bottom-4 sm:inset-auto sm:left-6 sm:bottom-6 sm:w-[400px] sm:max-w-[calc(100vw-3rem)] h-[80vh] sm:h-[600px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col z-50 overflow-hidden" data-testid="chatbot-panel">
          <div className="flex items-center justify-between px-4 py-3 bg-[#0A1128] text-white">
            <div className="flex items-center gap-2">
              <span className="h-9 w-9 rounded-full bg-[#0033A0] flex items-center justify-center">
                <Robot size={20} weight="fill" />
              </span>
              <div>
                <div className="font-display font-bold leading-none">Boot Buddy</div>
                <div className="text-xs text-slate-300 mt-0.5">Plain-language benefits help</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close" data-testid="chatbot-close-btn" className="text-slate-300 hover:text-white">
              <X size={20} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#0033A0] text-white rounded-br-sm"
                      : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
                  }`}
                >
                  {m.text || (busy && i === messages.length - 1 ? (
                    <span className="inline-flex gap-1">
                      <span className="dot inline-block h-1.5 w-1.5 rounded-full bg-slate-400" />
                      <span className="dot inline-block h-1.5 w-1.5 rounded-full bg-slate-400" />
                      <span className="dot inline-block h-1.5 w-1.5 rounded-full bg-slate-400" />
                    </span>
                  ) : null)}
                </div>
              </div>
            ))}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-slate-200 p-3 flex items-center gap-2 bg-white"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about pay, GI Bill, branches…"
              data-testid="chatbot-input"
              className="flex-1 px-3 py-2.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#0033A0] focus:border-transparent"
            />
            <button
              type="submit"
              disabled={busy || !input.trim()}
              data-testid="chatbot-send-btn"
              className="h-10 w-10 rounded-md bg-[#0033A0] hover:bg-[#002277] disabled:opacity-50 text-white flex items-center justify-center"
              aria-label="Send"
            >
              <PaperPlaneRight size={18} weight="fill" />
            </button>
          </form>
          <p className="text-[10px] text-slate-400 px-3 pb-2 text-center">
            AI-generated. Verify specific numbers and eligibility with official sources.
          </p>
        </div>
      )}
    </>
  );
}
